import * as React from 'react';
import {
  Table,
  Pagination,
  Empty,
  Typography,
  ColumnsType,
  Space,
} from '@revfluence/fresh';
import { RectangleAdIcon } from '@revfluence/fresh-icons/regular/esm';
import {
  ReconnectButton,
  RemoveButton,
  renderAdsInsightsTag,
  renderDraftAdsTag,
  renderAdPublishersTag,
  IAdPublisherWithPermissions,
} from '.';
import AdPublishersDrawer from './AdPublishersDrawer';
import { CellStatus } from './utils/composeCells';

import styles from './Tables.scss';

const { Title } = Typography;

export type DraftAdsFeatures = [
  { feature: 'Create Ads From Existing Posts', errorMessage: string },
];

export type AdsInsightsFeatures = [
  { feature: 'Viewing ad insight data', errorMessage: string },
];

interface IUpdateAccountInput {
  adAccountId: string,
  draftAdsEnabled?: boolean,
  defaultDraftAdAccount?: boolean,
}

export interface IAdAccount {
  id: string;
  name: string;
  networkId: string;
  socialAccountId: string;
  disconnectAccount: boolean;
  adsInsights: {
    enabled: boolean,
    cell: CellStatus,
    features: AdsInsightsFeatures,
    accessLost: boolean;
  },
  draftAds: {
    enabled: boolean,
    cell: CellStatus,
    features: DraftAdsFeatures,
    accessLost: boolean;
    defaultAccount: boolean;
  }
}

interface IAdAccountTableProps {
  data: IAdAccount[];
  pagination?: {
    total: number;
    current: number;
    pageSize: number;
  };
  AddAccountsButton: React.ReactNode;
  updateAccount: (input: IUpdateAccountInput) => void;
  removeAccount: (id: string) => void;
  reconnectAccount: () => void;
  onPaginationChange: (page: number) => void;
  draftAdsEnabled: boolean;
  currentUserId: string;
  isManagerAdmin: boolean;
  onPermissionsChange: (userId: string, permissions: string[], adAccount: IAdAccount) => void;
  users: any[];
}

export const AdAccountTable: React.FC<IAdAccountTableProps> = (props: IAdAccountTableProps) => {
  const {
    data,
    pagination,
    AddAccountsButton,
    updateAccount,
    removeAccount,
    reconnectAccount,
    onPaginationChange,
    draftAdsEnabled,
    currentUserId,
    isManagerAdmin,
    onPermissionsChange,
    users,
  } = props;
  const isEmpty = data.length === 0;
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedAccount, setSelectedAccount] = React.useState<IAdAccount | null>(null);

  // For the drawer, build a list of all users with their permissions for the selected ad account
  const allUsersWithPermissions = React.useMemo(() => {
    if (!selectedAccount) return [];
    return users.map((user) => {
      const perm = (user.userEntityPermissions || []).find((p) => p.entityId === selectedAccount.networkId);
      return {
        id: user.id,
        name: user.name,
        email: user.email,
        permissions: perm ? perm.permissions : [],
        profileImageUrl: user.auth0User?.picture,
      };
    });
  }, [users, selectedAccount]);

  const columns: ColumnsType<IAdAccount> = [
    {
      title: 'Ad Accounts',
      dataIndex: 'name',
      key: 'name',
      width: '20%',
    },
    {
      title: 'Ads Insights',
      dataIndex: 'adsInsights',
      key: 'insights',
      width: '20%',
      // render functions expects text as a parameter, so adding the _ to show it's being ignored
      render: (_, record: IAdAccount) => renderAdsInsightsTag(record),
    },
    ...(draftAdsEnabled ? [{
      title: 'Create Ads',
      dataIndex: 'draftAds',
      key: 'draftAds',
      width: '20%',
      // render functions expects text as a parameter, so adding the _ to show it's being ignored
      render: (_, record: IAdAccount) => renderDraftAdsTag(
        record,
        () => {
          updateAccount({
            adAccountId: record.id,
            draftAdsEnabled: true,
            defaultDraftAdAccount: record.draftAds.defaultAccount,
          });
        },
        () => {
          updateAccount({
            adAccountId: record.id,
            draftAdsEnabled: false,
            defaultDraftAdAccount: record.draftAds.defaultAccount,
          });
        },
        () => {
          updateAccount({
            adAccountId: record.id,
            draftAdsEnabled: true,
            defaultDraftAdAccount: true,
          });
        },
      ),
    }] : []),
    ...(draftAdsEnabled ? [{
      title: 'Ad Publishers',
      dataIndex: 'publishers',
      key: 'publishers',
      width: '20%',
      render: (_: unknown, record: IAdAccount) => {
        if (!record.draftAds.enabled) {
          return null;
        }
        // Build the publishers array for this ad account
        const publishers: IAdPublisherWithPermissions[] = users.map((user) => {
          const perm = (user.userEntityPermissions || []).find((p) => p.entityId === record.networkId);
          return {
            id: user.id,
            name: user.name,
            email: user.email,
            permissions: perm ? perm.permissions : [],
          };
        });
        return renderAdPublishersTag({ ...record, publishers }, () => {
          setSelectedAccount(record);
          setDrawerOpen(true);
        });
      },
    }] : []),
    {
      key: 'actions',
      width: '20%',
      // render functions expects text as a parameter, so adding the _ to show it's being ignored
      render: (_text: unknown, record: IAdAccount) => (
        <Space>
          {record.disconnectAccount
            && <ReconnectButton onClick={reconnectAccount} disconnectAccount />}
          <RemoveButton removeAccount={() => removeAccount(record.id)} />
        </Space>
      ),
    },
  ];

  if (isEmpty) {
    return (
      <>
        <Title level={5} className={styles.TableTitle}>Connected Ad Accounts</Title>
        <Empty
          size="small"
          image={<RectangleAdIcon />}
          description={(
            <>
              <Typography.Title level={5}>
                No connected ad accounts
              </Typography.Title>
              <Typography.Paragraph type="secondary">
                Connect your Ad Accounts to view ad insights and create ads.
              </Typography.Paragraph>
            </>
          )}
        />
        {AddAccountsButton}
      </>
    );
  }

  return (
    <>
      <Title level={5} className={styles.TableTitle}>Connected Ad Accounts</Title>
      <Table
        className={styles.InstagramTable}
        rowClassName={() => styles.RowTable}
        columns={columns}
        dataSource={data}
        pagination={false}
        footer={() => (
          <div className={styles.InstagramTableFooter}>
            {AddAccountsButton}
            {pagination && <Pagination {...pagination} onChange={onPaginationChange} hideOnSinglePage />}
          </div>
        )}
      />
      <AdPublishersDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        users={allUsersWithPermissions}
        currentUserId={currentUserId}
        isManagerAdmin={isManagerAdmin}
        onPermissionsChange={(userId, permissions) => {
          if (selectedAccount) {
            onPermissionsChange(userId, permissions, selectedAccount);
          }
        }}
      />
    </>
  );
};
